import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { Extension } from 'src/app/extension/model/extension';
import { MessengerService } from './messenger.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { DictionaryService } from './dictionary.service';

@Injectable({
  providedIn: 'root'
})
export class ExtensionService implements OnDestroy
{
  /**
   * List of Extension categories
   */
  public extensionCategories: Array<{ text: string; key: string; value: string }> = [
    { text: 'Care Team', key: 'CARE-TEAM', value: 'CARE_TEAM' },
    { text: 'Case', key: 'CASE', value: 'CASE' },
    { text: 'Patient', key: 'PATIENT', value: 'PATIENT' },
    { text: 'Community Service', key: 'COMMUNITY-SERVICE', value: 'COMMUNITY_SERVICE' },
    { text: 'Organization', key: 'ORGANIZATION', value: 'ORGANIZATION' },
  ];

  public textYES = '';
  public textNO = '';

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private messengerService: MessengerService,
    private dictionaryService: DictionaryService,
    private intl: IntlService,
    private translate: TranslateService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get Extension by id.
   *
   * @param id Extension id
   */
  public getExtension(id: number): Observable<any>
  {
    return this.http.get<any>(`/setting/extension/${id}`);
  }

  /**
   * Save a Extension.
   *
   * @param data Extension to persit
   * @param isNew Indicate if this is a new or update
   */
  public saveExtension(data: Extension): Observable<any>
  {
    return this.http.post('/setting/extension', data);
  }

  /**
   * Delete Extension by id.
   *
   * @param id Extension id
   */
  public deleteExtension(id: number): Observable<any>
  {
    return this.http.delete<any>(`/setting/extension/${id}`);
  }

  /**
   * Get Extension in use.
   *
   * @param id Extension id
   */
  public isExtensionInUse(id: number): Observable<boolean>
  {
    return this.http.get<any>(`/setting/extension/inUse/${id}`);
  }

  public isExtensionByCategoryInUse(id: number, category: string): Observable<boolean>
  {
    return this.http.get<any>(`/setting/extension/inUse/${id}/${category}`);
  }

  /**
   * Get distinct Organizations by Extension Category id.
   *
   * @param category Extension Category
   */
  public getDistinctExtensionOrganizations(category: string): Observable<any>
  {
    return this.http.get<any>(`/setting/extension/organizations?category=${category}`);
  }

  /**
   * Get all setting extensions for a specific extension category.
   *
   * @param category Extension Category
   */
  public getExtensionsByCategory(category: string, allOrganizations = false, billableItem = false): Observable<any>
  {
    const organizationQueryString = allOrganizations ? `&allOrganizationsOnly=true` : '';
    const billableItemQueryString = billableItem ? `&billableItemOnly=true` : '';
    return this.http.get<any>(`/setting/extension/categories?category=${category}${organizationQueryString}${billableItemQueryString}`);
  }

  public getAllOrganizationExtensions(): Observable<any[]>
  {
    const patientExtensions = this.getExtensionsByCategory('PATIENT', true);
    const caseExtensions = this.getExtensionsByCategory('CASE', true);
    return forkJoin([patientExtensions, caseExtensions]);
  }

  public formatExtensionValue(dataItem: any): any 
  {
    let value = dataItem.value;
    const isBooleanValue = value && (dataItem?.fieldType === 'checkbox' || dataItem?.fieldType === 'multicheckbox' || value === 'true' || value === 'false');
    const isDateValue = value && (dataItem?.fieldType === 'date' || (value.length === 10 && value[4] === '-' && value[7] === '-'));
    const isDateTimeValue = value && (dataItem?.fieldType === 'datetime' || (value.length > 11 && value[4] === '-' && value[7] === '-' && value[10] === 'T' && value.includes('Z')));

    if (isBooleanValue) 
    {
      value = value.replaceAll('true', this.textYES);
      value = value.replaceAll('false', this.textNO);
    }

    if (isDateValue)
    {
      value = this.intl.formatDate(this.intl.parseDate(value), 'd');
    }

    if (isDateTimeValue)
    {
      // If UTC, must convert to local date
      if (value.includes('Z'))
      {
        value = this.intl.formatDate(new Date(value), 'g');
      }
      else
      {
        value = this.intl.formatDate(this.intl.parseDate(value), 'g');
      }
    }
    return value;
  }

  public refreshExtensionDisplayNames(value: string, extensionFormGroup: any, viewExtensions: any): void
  {
    if (extensionFormGroup && extensionFormGroup.get('codeSetId').value)
    {
      const codes = value.split(',');
      let displayNames = '';
      let alternateValues = '';
      codes.forEach((code) =>
      {
        this.dictionaryService.getCachedCodesByCodeSetId(extensionFormGroup.get('codeSetId').value)
          .subscribe({
            next: (data) =>
            {
              const lookup = data.find(x => x.code === code);
              if (lookup)
              {
                displayNames += (displayNames ? ' | ' : '') + lookup.displayName;
                if (lookup.alternateCode)
                {
                  alternateValues += (alternateValues ? ' | ' : '') + lookup.alternateCode;
                }
              }
              // Update the current editing row
              const currentRecord = viewExtensions.data.find(item => item.id === extensionFormGroup.get('id').value);
              if (currentRecord)
              {
                currentRecord.displayName = displayNames;
                currentRecord.alternateValue = alternateValues;
              }
            }
          });
      })
    }
  }

  private loadLocalizations(): void
  {
    this.translate.get(['CARE-TEAM', 'CASE', 'PATIENT', 'YES', 'NO', 'COMMUNITY-SERVICE', 'ORGANIZATION']).subscribe({
      next: (res) =>
      {
        this.textYES = res.YES;
        this.textNO = res.NO;
        this.extensionCategories[0].text = res['CARE-TEAM'];
        this.extensionCategories[1].text = res.CASE;
        this.extensionCategories[2].text = res.PATIENT;
        this.extensionCategories[3].text = res['COMMUNITY-SERVICE'];
        this.extensionCategories[4].text = res.ORGANIZATION;
      }
    });
  }
}
